import React from 'react';
import { ShotVersion } from '../../../types';
import { formatDate } from '../../../utils/formatDate';
import { highlightAtRefs } from '../../../utils/highlightAtRefs';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ShotVersionDetailPanelProps {
  version: ShotVersion;
  expanded: boolean;
  t: Translate;
  onToggle: () => void;
}

export default function ShotVersionDetailPanel({
  version,
  expanded,
  t,
  onToggle,
}: ShotVersionDetailPanelProps) {
  const handleCopyTaskId = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!version.videoTaskId) return;
    navigator.clipboard?.writeText(version.videoTaskId).catch(() => {});
  };

  return (
    <>
      <button
        onClick={(event) => { event.stopPropagation(); onToggle(); }}
        style={{ width: '100%', background: 'none', border: '1px solid var(--border)', borderRadius: '4px', padding: '3px 0', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--text-2)' }}
      >
        {expanded ? t('version.collapseDetail') : t('version.expandDetail')}
      </button>

      {expanded && (
        <div style={{ marginTop: '0.5rem' }}>
          {version.videoTaskId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', margin: '0 0 0.2rem 0', minWidth: 0 }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={version.videoTaskId}>
                {t('version.taskId', { id: version.videoTaskId })}
              </span>
              <button
                type="button"
                aria-label={t('version.copyTaskId')}
                title={t('version.copyTaskId')}
                onClick={handleCopyTaskId}
                style={{ flexShrink: 0, background: 'none', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'copy', padding: '1px 6px', fontSize: '0.68rem', color: 'var(--text-2)' }}
              >
                {t('version.copy')}
              </button>
            </div>
          )}
          <p style={{ margin: '0 0 0.2rem 0', fontSize: '0.72rem', color: 'var(--text-3)' }}>{t('version.submittedAt', { time: formatDate(version.createdAt) })}</p>
          {version.completedAt && (
            <p style={{ margin: '0 0 0.2rem 0', fontSize: '0.72rem', color: 'var(--text-3)' }}>{t('version.completedAt', { time: formatDate(version.completedAt), seconds: Math.round((new Date(version.completedAt).getTime() - new Date(version.createdAt).getTime()) / 1000) })}</p>
          )}
          <p style={{ margin: '0 0 0.2rem 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>{t('version.durationAndDesc', { duration: version.duration, desc: version.description || t('version.noDescription') })}</p>
          {version.taskStatus === 'failed' && version.errorMsg && <p style={{ margin: '0.3rem 0 0 0', fontSize: '0.75rem', color: 'var(--danger)' }}>❌ {version.errorMsg}</p>}
          {version.prompt && (
            <pre style={{ margin: '0.4rem 0 0 0', fontSize: '0.77rem', background: 'var(--surface-2)', padding: '0.4rem', borderRadius: '4px', whiteSpace: 'pre-wrap', fontFamily: 'inherit', wordBreak: 'break-all' }}>{highlightAtRefs(version.prompt)}</pre>
          )}
        </div>
      )}
    </>
  );
}
